/**
 * Capture the rendered geometry of every table in the editor so it can be persisted as
 * `data-tablewidth` / `data-wrapperwidth` and later replayed by `tableUtils.js`.
 */

const TABLE_WRAPPER_SELECTOR = '.tableWrapper';

const toPixels = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value !== 'string' || value.trim() === '' || value.includes('%')) {
    return 0;
  }
  return parseInt(value, 10) || 0;
};

/**
 * Read one wrapper's geometry.
 *
 * `tableWidth` is the table's own rendered width — the sum of its column widths, which is what
 * the stored per-column percentages must be relative to. It is 0 when TipTap has not given the
 * table an explicit width (i.e. not every column has been resized yet), in which case the table
 * simply fills its wrapper and the wrapper width is the correct denominator.
 */
const getWrapperWidths = (wrapper) => {
  const table = wrapper.querySelector('table');

  return {
    tableWidth: table ? toPixels(table.style.width) : 0,
    wrapperWidth: toPixels(wrapper.clientWidth)
  };
};

/**
 * Walk `dom` and return one `{ tableWidth, wrapperWidth }` entry per table wrapper, in document
 * order — the same order `document.getElementsByTagName('table')` yields, so callers can zip the
 * two by index.
 */
export const collectTableWidths = (dom) => {
  const wrappers = Array.from(dom.querySelectorAll(TABLE_WRAPPER_SELECTOR));

  // `querySelectorAll` only sees descendants. In production `dom` is the ProseMirror editor root,
  // which is never a wrapper, but counting it when it is one costs a line and removes a silent
  // difference from the recursive walk this replaced.
  if (typeof dom.matches === 'function' && dom.matches(TABLE_WRAPPER_SELECTOR)) {
    wrappers.unshift(dom);
  }

  return wrappers.map(getWrapperWidths);
};

export default collectTableWidths;
